import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '@/components/ui/drawer';
import { useApp } from '@/context/app';
import { api } from '@/lib/api';
import { Button } from '@hubmaster/ui';
import { useMutation } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { QuantityInput } from '../QuantityInput';
import { Input } from './Input';
import { formatDecimals } from './functions';

type Operation = 'absolute' | 'sum' | 'subtract';

const OPERATION_LABELS: Record<Operation, string> = {
	absolute: 'Absoluto',
	sum: '+ Somar',
	subtract: '− Subtrair',
};

export function EditProduct({
	data,
	onSuccess,
}: {
	data: any;
	onSuccess?: (updates: { price?: number; stock?: number }) => void;
}) {
	const { auth, connection } = useApp();
	const [open, setOpen] = useState(false);
	const [operation, setOperation] = useState<Operation>('absolute');
	const [stockQty, setStockQty] = useState<number>(0);

	const canEditPrice = connection.functions?.includes('edit_price');
	const canEditStock = connection.functions?.includes('edit_stock');

	const { handleSubmit, register } = useForm();

	useEffect(() => {
		if (!open) return;
		setOperation('absolute');
		setStockQty(data.stock ?? 0);
	}, [open]);

	useEffect(() => {
		setStockQty(operation === 'absolute' ? (data.stock ?? 0) : 0);
	}, [operation]);

	const mutation = useMutation({
		mutationFn: async (form: any) => {
			const parsedPrice = canEditPrice
				? parseFloat(
						String(form.price ?? '')
							.replace(/\./g, '')
							.replace(',', '.'),
					)
				: undefined;

			const priceChanged =
				parsedPrice !== undefined &&
				!isNaN(parsedPrice) &&
				parsedPrice !== data.price;

			const stockChanged =
				canEditStock &&
				(operation !== 'absolute'
					? stockQty > 0
					: stockQty !== (data.stock ?? 0));

			if (!priceChanged && !stockChanged) {
				throw { data: { message: 'Nenhuma alteração detectada' } };
			}

			const finalStock = stockChanged
				? operation === 'sum'
					? (data.stock ?? 0) + stockQty
					: operation === 'subtract'
						? (data.stock ?? 0) - stockQty
						: stockQty
				: undefined;

			const body: Record<string, any> = { id: data.id };
			if (priceChanged) body.price = parsedPrice;
			if (finalStock !== undefined) body.stock = finalStock;

			const response = await api.put(`${auth.token}/products`, body);
			return { response: response.data, priceChanged, parsedPrice, finalStock };
		},
		onSuccess: ({ response, priceChanged, parsedPrice, finalStock }) => {
			const updates: { price?: number; stock?: number } = {};
			if (priceChanged) updates.price = parsedPrice;
			if (finalStock !== undefined) updates.stock = finalStock;
			onSuccess?.(updates);
			toast.success(response.message ?? 'Salvo com sucesso!');
			setOpen(false);
		},
		onError: (res: any) => {
			toast.error(res.data?.message ?? 'Erro ao salvar');
			setOpen(false);
		},
	});

	if (!canEditPrice && !canEditStock) return null;

	const estimatedStock =
		operation === 'sum'
			? (data.stock ?? 0) + stockQty
			: operation === 'subtract'
				? (data.stock ?? 0) - stockQty
				: stockQty;

	return (
		<Drawer open={open} onOpenChange={setOpen}>
			<DrawerTrigger asChild>
				<Button variant={'ghost'} className="ml-1 p-2">
					<Pencil />
				</Button>
			</DrawerTrigger>
			<DrawerContent>
				<form onSubmit={handleSubmit((form) => mutation.mutate(form))}>
					<DrawerHeader className="mb-3 gap-0">
						<DrawerTitle className="text-sm">Editar produto:</DrawerTitle>
						<DrawerDescription>{data.name}</DrawerDescription>
					</DrawerHeader>

					<div className="flex flex-col gap-6 px-4">
						{canEditPrice && (
							<div className="flex flex-col items-center gap-2">
								<p className="text-xs font-semibold tracking-wide text-zinc-400 uppercase">
									Preço
								</p>
								<Input price={data.price} register={register} />
							</div>
						)}

						{canEditPrice && canEditStock && <hr className="border-zinc-100" />}

						{canEditStock && (
							<div className="flex flex-col items-center gap-3">
								<p className="text-xs font-semibold tracking-wide text-zinc-400 uppercase">
									Estoque
								</p>

								<div className="border-primary flex w-28 items-center border-b-2 p-1">
									<QuantityInput
										value={stockQty}
										decimals={2}
										onChange={(val) => setStockQty(Math.max(0, val))}
										className="w-full border-none text-center text-xl outline-none"
									/>
								</div>

								<div className="flex gap-2">
									{(['absolute', 'sum', 'subtract'] as Operation[]).map(
										(op) => (
											<button
												key={op}
												type="button"
												onClick={() => setOperation(op)}
												className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
													operation === op
														? 'border-primary bg-primary text-white'
														: 'border-zinc-200 text-zinc-500 hover:border-zinc-400'
												}`}
											>
												{OPERATION_LABELS[op]}
											</button>
										),
									)}
								</div>
								{operation !== 'absolute' && (
									<div className="flex flex-col items-center gap-1">
										<p className="text-xs text-zinc-500">
											Estoque atual:{' '}
											<strong>{formatDecimals(data.stock ?? 0)}</strong>
										</p>
										<p
											className={`text-xs font-medium ${
												estimatedStock < 0 ? 'text-amber-500' : 'text-zinc-500'
											}`}
										>
											Resultado estimado:{' '}
											<strong>{formatDecimals(estimatedStock)}</strong>
											{estimatedStock < 0 && ' ⚠ ficará negativo'}
										</p>
									</div>
								)}
							</div>
						)}
					</div>

					<DrawerFooter className="mt-6 flex-row justify-end">
						<DrawerClose asChild>
							<Button variant="outline">Cancelar</Button>
						</DrawerClose>
						<Button type="submit" isLoading={mutation.isPending}>
							Salvar
						</Button>
					</DrawerFooter>
				</form>
			</DrawerContent>
		</Drawer>
	);
}

export { EditProduct as EditPrice };
